import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '@/components/Sidebar';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { maskPhone, onlyDigits } from '@/lib/formatters';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Calculator, Plus, Edit, FileText, Loader2 } from 'lucide-react';

const EMPTY_FORM = {
  nome: '',
  cpf_cnpj: '',
  email: '',
  whatsapp: '',
  empresas_atendidas: [],
};

const Contadores = ({ user, onLogout }) => {
  const company = JSON.parse(localStorage.getItem('company') || '{}');
  const navigate = useNavigate();

  const [contadores, setContadores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingContador, setEditingContador] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    if (company?.id) {
      fetchContadores();
    }
  }, [company?.id]);

  const fetchContadores = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/contadores/${company.id}`);
      setContadores(response.data);
    } catch (error) {
      toast.error('Erro ao carregar contadores');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (contador = null) => {
    if (contador) {
      setEditingContador(contador);
      setFormData({
        nome: contador.nome || '',
        cpf_cnpj: contador.cpf_cnpj || '',
        email: contador.email || '',
        whatsapp: maskPhone(contador.whatsapp?.length === 13 ? contador.whatsapp.slice(2) : contador.whatsapp || ''),
        empresas_atendidas: contador.empresas_atendidas || [],
      });
    } else {
      setEditingContador(null);
      setFormData(EMPTY_FORM);
    }
    setShowModal(true);
  };

  const toggleEmpresaAtendida = (valor) => {
    setFormData((prev) => {
      const atual = prev.empresas_atendidas;
      const novo = atual.includes(valor)
        ? atual.filter((v) => v !== valor)
        : [...atual, valor];
      return { ...prev, empresas_atendidas: novo };
    });
  };

  const paraE164 = (whatsapp) => {
    const numero = onlyDigits(whatsapp);
    if (numero.length === 13) return numero; // já inclui 55
    if (numero.length === 11) return `55${numero}`;
    return numero;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nome.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    if (formData.empresas_atendidas.length === 0) {
      toast.error('Selecione ao menos uma empresa atendida (Matriz/Filial)');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        empresa_id: company.id,
        ...formData,
        whatsapp: paraE164(formData.whatsapp),
        ativo: editingContador ? editingContador.ativo : true,
      };

      if (editingContador) {
        await axiosInstance.put(`/contadores/${editingContador.id}`, payload);
        toast.success('Contador atualizado!');
      } else {
        await axiosInstance.post('/contadores', payload);
        toast.success('Contador cadastrado!');
      }

      setShowModal(false);
      fetchContadores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar contador');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAtivo = async (contador) => {
    try {
      if (contador.ativo) {
        await axiosInstance.delete(`/contadores/${contador.id}`);
        toast.success('Contador desativado!');
      } else {
        await axiosInstance.put(`/contadores/${contador.id}`, {
          empresa_id: contador.empresa_id,
          nome: contador.nome,
          cpf_cnpj: contador.cpf_cnpj,
          email: contador.email,
          whatsapp: contador.whatsapp,
          empresas_atendidas: contador.empresas_atendidas,
          ativo: true,
        });
        toast.success('Contador reativado!');
      }
      fetchContadores();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  return (
    <div className="flex min-h-screen bg-zinc-950 text-white">
      <Sidebar user={user} onLogout={onLogout} activePage="contadores" />

      <div className="flex-1 p-8">
        <div className="space-y-6">
          <SubscriptionCard user={user} />

          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Calculator className="w-8 h-8 text-orange-400" />
                Contadores
              </h1>
              <p className="text-zinc-400 mt-1">Cadastro de contadores para envio mensal de notas fiscais</p>
            </div>
            <Button onClick={() => handleOpenModal()} className="bg-orange-600 hover:bg-orange-700">
              <Plus className="w-4 h-4 mr-2" /> Novo Contador
            </Button>
          </div>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>Lista de Contadores</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
                </div>
              ) : contadores.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhum contador cadastrado</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800">
                        <TableHead>Nome</TableHead>
                        <TableHead>CPF/CNPJ</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>WhatsApp</TableHead>
                        <TableHead>Empresas Atendidas</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contadores.map((contador) => (
                        <TableRow key={contador.id} className="border-zinc-800 hover:bg-zinc-800/50">
                          <TableCell className="font-medium">{contador.nome}</TableCell>
                          <TableCell>{contador.cpf_cnpj}</TableCell>
                          <TableCell>{contador.email}</TableCell>
                          <TableCell>{contador.whatsapp}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {contador.empresas_atendidas.map((e) => (
                                <Badge key={e} variant="outline" className="border-blue-500/50 text-blue-400 capitalize">
                                  {e}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={contador.ativo
                                ? 'border-green-500/50 text-green-400 cursor-pointer hover:bg-green-500/10'
                                : 'border-zinc-500/50 text-zinc-400 cursor-pointer hover:bg-zinc-500/10'
                              }
                              onClick={() => handleToggleAtivo(contador)}
                            >
                              {contador.ativo ? 'Ativo' : 'Inativo'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/contadores/${contador.id}`)}
                                className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                                title="Ver notas fiscais"
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenModal(contador)}
                                className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-orange-400" />
              {editingContador ? 'Editar Contador' : 'Novo Contador'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="nome">Nome *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                placeholder="Nome do contador"
                required
              />
            </div>

            <div>
              <Label htmlFor="cpf_cnpj">CPF/CNPJ *</Label>
              <Input
                id="cpf_cnpj"
                value={formData.cpf_cnpj}
                onChange={(e) => setFormData({ ...formData, cpf_cnpj: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                placeholder="000.000.000-00"
                required
              />
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                placeholder="contador@escritorio.com"
                required
              />
            </div>

            <div>
              <Label htmlFor="whatsapp">WhatsApp *</Label>
              <Input
                id="whatsapp"
                value={formData.whatsapp}
                onChange={(e) => setFormData({ ...formData, whatsapp: maskPhone(e.target.value) })}
                className="bg-zinc-800 border-zinc-700"
                placeholder="(51) 99999-9999"
                required
              />
              <p className="text-xs text-zinc-500 mt-1">Usado para gerar o link de envio via WhatsApp</p>
            </div>

            <div>
              <Label>Empresas Atendidas *</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={formData.empresas_atendidas.includes('matriz')}
                    onCheckedChange={() => toggleEmpresaAtendida('matriz')}
                  />
                  Matriz
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={formData.empresas_atendidas.includes('filial')}
                    onCheckedChange={() => toggleEmpresaAtendida('filial')}
                  />
                  Filial
                </label>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="border-zinc-700">
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} className="bg-orange-600 hover:bg-orange-700">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingContador ? 'Salvar' : 'Cadastrar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Contadores;
